<template>
  <QuestionPadding>
    <HintText>{{ c('ranked.drag_to_reorder') }}</HintText>
    <ScrollIntersectionObserver>
      <DraggableList
        ref="draggable-list"
        :list="rankedFields"
        class="ranked-question__container"
        item-class="ranked-question__field"
        ghost-class="ranked-question__field--grabbed"
        drag-class="ranked-question__ghost--hidden"
        :animation="150"
        item-key="id"
        active-cursor="grabbing"
        data-test-id="RankedQuestion_DraggableList"
        @click="() => handleAnswersUpdated(rankedFields as Field<'ranked'>[])"
        @change="e => reorderFields(e as DragEventChange<Field<'ranked'>>)"
      >
        <template #item="{ element, index }">
          <div
            :class="{
              'ranked-question__field-index': true,
              'ranked-question__field-index--rtl': isRtl,
            }"
          >
            {{ index + 1 }}
          </div>
          <p class="ranked-question__field-text" :dir="isRtl ? 'rtl' : 'ltr'">
            {{ element.title }}
          </p>
          <img class="ranked-question__grip-icon" :src="GripIcon" />
        </template>
      </DraggableList>
    </ScrollIntersectionObserver>
  </QuestionPadding>
</template>
<script lang="ts">
import { move } from 'ramda'
import { computed, type DeepReadonly, type PropType } from 'vue'

import { type Answer, assertAnswerType } from '../../../answer/models/Answer'
import { c } from '../../../intl'
import { isRtlLocale } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import QuestionPadding from '../../../question/components/dumb/QuestionPadding.vue'
import { type Field } from '../../../question/models/Field'
import { isSpecialFieldId } from '../../../question/models/Field'
import { type Question } from '../../../question/models/Question'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'
import GripIcon from '../../assets/grip.svg'
import { type DragEventChange } from '../dumb/DraggableList.vue'
import DraggableList from '../dumb/DraggableList.vue'

export default {
  components: {
    DraggableList,
    HintText,
    QuestionPadding,
    ScrollIntersectionObserver,
  },
  props: {
    question: {
      type: Object as PropType<DeepReadonly<Pick<Question<'ranked'>, 'fields'>>>,
      required: true,
    },
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
  },
  emits: {
    answersUpdated: (_newAnswers: Answer[]) => true,
  },
  setup(props, ctx) {
    const rankedFields = computed(() =>
      [...props.answers]
        .flatMap(ans => (ans.type === 'ranked' ? [ans] : []))
        .sort((a, b) => a.value.ranking - b.value.ranking)
        .map(answer => props.question.fields.find(field => field.id === answer.fieldId))
        .flatMap(field => (field?.type === 'ranked' ? [field] : [])),
    )

    const reorderFields = (e: DragEventChange<Field<'ranked'>>): void => {
      const rankedFieldsUpdated = move(e.oldIndex, e.newIndex, [...rankedFields.value])
      handleAnswersUpdated(rankedFieldsUpdated)
    }

    const handleAnswersUpdated = (fields: Field<'ranked'>[]): void => {
      const answers = props.answers.map(answer => {
        if (isSpecialFieldId(answer.fieldId)) {
          assertAnswerType(answer.type, 'choice')
          return { ...answer, value: { selected: false } }
        }

        assertAnswerType(answer.type, 'ranked')
        return {
          ...answer,
          value: { ranking: fields.findIndex(field => field.id === answer.fieldId) },
        }
      })

      ctx.emit('answersUpdated', answers)
    }

    return {
      rankedFields,
      GripIcon,
      reorderFields,
      handleAnswersUpdated,
      c,
      isRtl: isRtlLocale(),
    }
  },
}
</script>
<style lang="postcss">
.ranked-question {
  &__container {
    cursor: grab;
  }
  &__field {
    background-color: white;
    border: var(--color-blue-grey) 1px solid;
    user-select: none;
    display: grid;
    grid-template-columns: 3rem 1fr auto;
    &--grabbed {
      background-color: #f6f9fd;
      box-shadow: 0 0 6px 2px rgba(0, 0, 0, 0.162);
      z-index: 10;
      cursor: grabbing;
    }
    &:not(:last-child) {
      border-bottom: 0;
    }
  }
  &__field-text {
    padding: 1rem;
  }
  &__field-index {
    padding: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f6f9fd;
    border-right: 1px solid var(--color-blue-grey);

    &--rtl {
      border-right: 0;
      border-left: 1px solid var(--color-blue-grey);
    }
  }
  &__grip-icon {
    align-self: center;
    padding: 1rem;
  }
  &__ghost--hidden {
    opacity: 0 !important;
    cursor: grabbing;
  }
}
</style>
